import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';
import ConfirmationModal from './ConfirmationModal';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  useMediaQuery,
  Snackbar,
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SubmitIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  CheckCircle as SubmittedIcon,
  Replay as ReturnedIcon,
} from '@mui/icons-material';

interface Field {
  id: string;
  type: 'text_line' | 'textarea' | 'checkbox';
  x_pct: number;
  y_pct: number;
  width_pct: number;
  height_pct: number;
  label: string;
}

interface PageData {
  page_number: number;
  background_image_url: string;
  image_width: number;
  image_height: number;
  fields: Field[];
}

const InteractiveAssignment: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [tailoredDocumentId, setTailoredDocumentId] = useState<number | null>(null);
  const [pages, setPages] = useState<PageData[]>([]);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<string>('draft');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState(false);
  const [saveNotice, setSaveNotice] = useState(false);
  const [teacherFeedback, setTeacherFeedback] = useState('');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (assignmentId) loadInteractiveContent();
  }, [assignmentId]);

  const loadInteractiveContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const resp = await axios.get(
        `${API_BASE_URL}/api/student/assignments/${assignmentId}/interactive`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPages(resp.data.pages || []);
      setDocumentName(resp.data.document_name || '');
      setTailoredDocumentId(resp.data.tailored_document_id);

      if (resp.data.existing_response) {
        setResponses(resp.data.existing_response.responses || {});
        setStatus(resp.data.existing_response.status || 'draft');
        setTeacherFeedback(resp.data.existing_response.teacher_feedback || '');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('interactive.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const updateField = useCallback((fieldId: string, value: string) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      autoSave({ ...responses, [fieldId]: value });
    }, 3000);
  }, [responses, tailoredDocumentId, assignmentId]);

  const autoSave = async (currentResponses: Record<string, string>) => {
    if ((status !== 'draft' && status !== 'returned') || !tailoredDocumentId) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API_BASE_URL}/api/student/assignments/${assignmentId}/interactive-response`,
        {
          tailored_document_id: tailoredDocumentId,
          responses: currentResponses,
          action: 'save',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaveNotice(true);
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  const handleSave = async () => {
    if (!tailoredDocumentId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API_BASE_URL}/api/student/assignments/${assignmentId}/interactive-response`,
        {
          tailored_document_id: tailoredDocumentId,
          responses,
          action: 'save',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaveNotice(true);
    } catch (err: any) {
      setError(err.response?.data?.error || t('interactive.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!tailoredDocumentId) return;
    setSubmitting(true);
    setSubmitConfirm(false);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API_BASE_URL}/api/student/assignments/${assignmentId}/interactive-response`,
        {
          tailored_document_id: tailoredDocumentId,
          responses,
          action: 'submit',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus('submitted');
    } catch (err: any) {
      setError(err.response?.data?.error || t('interactive.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/student/assignments')}>
          {t('interactive.backToAssignments')}
        </Button>
      </Box>
    );
  }

  const isSubmitted = status === 'submitted';
  const isReturned = status === 'returned';
  const isLocked = isSubmitted;
  const isEditable = !isLocked;

  return (
    <Box sx={{ p: isMobile ? 1 : 3, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/student/assignments')}
          size="small"
          aria-label={t('interactive.backToAssignments')}
        >
          {t('interactive.back')}
        </Button>
        <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ flex: 1 }}>
          {documentName}
        </Typography>
        {isSubmitted ? (
          <Chip
            icon={<SubmittedIcon />}
            label={t('interactive.submitted')}
            color="success"
            size="small"
          />
        ) : isReturned ? (
          <Chip
            icon={<ReturnedIcon />}
            label={t('interactive.returned')}
            color="warning"
            size="small"
          />
        ) : (
          <Chip
            icon={<EditIcon />}
            label={t('interactive.draft')}
            color="warning"
            size="small"
          />
        )}
      </Box>

      {isSubmitted && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('interactive.submittedMessage')}
        </Alert>
      )}

      {isReturned && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('interactive.returnedMessage')}
          {teacherFeedback && (
            <Box sx={{ mt: 1, fontStyle: 'italic' }}>
              <strong>{t('interactive.teacherFeedback')}:</strong> {teacherFeedback}
            </Box>
          )}
        </Alert>
      )}

      {/* Pages with interactive fields */}
      {pages.map(page => (
        <Paper
          key={page.page_number}
          elevation={2}
          sx={{ mb: 2, position: 'relative', overflow: 'hidden', borderRadius: 1 }}
        >
          <Box sx={{ position: 'relative', width: '100%' }}>
            <img
              src={page.background_image_url}
              alt={`${t('interactive.page')} ${page.page_number}`}
              style={{ width: '100%', display: 'block' }}
              draggable={false}
            />
            {page.fields.map(field => (
              <Box
                key={field.id}
                sx={{
                  position: 'absolute',
                  left: `${field.x_pct}%`,
                  top: `${field.y_pct}%`,
                  width: `${field.width_pct}%`,
                  height: `${field.height_pct}%`,
                }}
              >
                {field.type === 'text_line' ? (
                  <input
                    type="text"
                    value={responses[field.id] || ''}
                    onChange={e => updateField(field.id, e.target.value)}
                    disabled={isLocked}
                    aria-label={field.label || `${t('interactive.answerField')} ${field.id}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: isReturned ? '1px solid #0288d1' : '1px solid #bdbdbd',
                      borderRadius: 3,
                      background: isLocked ? 'rgba(232,245,233,0.9)' : isReturned ? 'rgba(227,242,253,0.92)' : 'rgba(255,255,255,0.92)',
                      padding: '2px 6px',
                      fontSize: 'clamp(10px, 1.4vw, 16px)',
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                  />
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={responses[field.id] || ''}
                    onChange={e => updateField(field.id, e.target.value)}
                    disabled={isLocked}
                    aria-label={field.label || `${t('interactive.answerArea')} ${field.id}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: isReturned ? '1px solid #0288d1' : '1px solid #bdbdbd',
                      borderRadius: 3,
                      background: isLocked ? 'rgba(232,245,233,0.9)' : isReturned ? 'rgba(227,242,253,0.92)' : 'rgba(255,255,255,0.92)',
                      padding: '4px 6px',
                      fontSize: 'clamp(10px, 1.4vw, 14px)',
                      boxSizing: 'border-box',
                      resize: 'none',
                      outline: 'none',
                      lineHeight: 1.4,
                    }}
                  />
                ) : (
                  <input
                    type="checkbox"
                    checked={responses[field.id] === 'true'}
                    onChange={e => updateField(field.id, String(e.target.checked))}
                    disabled={isLocked}
                    aria-label={field.label || `${t('interactive.checkbox')} ${field.id}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      cursor: isLocked ? 'default' : 'pointer',
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      ))}

      {/* Action buttons */}
      {isEditable && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2, mb: 4, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || submitting}
            aria-label={t('interactive.saveDraft')}
          >
            {saving ? <CircularProgress size={20} /> : t('interactive.saveDraft')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SubmitIcon />}
            onClick={() => setSubmitConfirm(true)}
            disabled={saving || submitting}
            aria-label={isReturned ? t('interactive.resubmit') : t('interactive.submit')}
          >
            {submitting ? <CircularProgress size={20} /> : (isReturned ? t('interactive.resubmit') : t('interactive.submit'))}
          </Button>
        </Box>
      )}

      {/* Submit confirmation modal */}
      <ConfirmationModal
        isOpen={submitConfirm}
        title={t('interactive.submitConfirmTitle')}
        message={t('interactive.submitConfirmMessage')}
        confirmText={t('interactive.submit')}
        cancelText={t('interactive.cancel')}
        onConfirm={handleSubmit}
        onCancel={() => setSubmitConfirm(false)}
      />

      {/* Auto-save snackbar */}
      <Snackbar
        open={saveNotice}
        autoHideDuration={2000}
        onClose={() => setSaveNotice(false)}
        message={t('interactive.autoSaved')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default InteractiveAssignment;
